// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — Run Detail Page
// ═══════════════════════════════════════════════════════════

import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { cn, formatDuration, getPassRate } from '@/lib/utils';
import { Card, Badge, PageHeader, ProgressBar, Skeleton, Button } from '@/components/ui';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  SkipForward,
  Clock,
  ChevronDown,
  ChevronRight,
  Camera,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

export default function RunDetailPage() {
  const { t } = useTranslation();
  const { id, runId } = useParams<{ id: string; runId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projectId = parseInt(id!);
  const runIdNum = parseInt(runId!);
  const prevStatusRef = useRef<string | null>(null);

  const { data: run, isLoading } = useQuery({
    queryKey: ['run', runIdNum],
    queryFn: () => api.getRun(runIdNum),
    refetchInterval: (data) =>
      data?.state?.data?.runStatus === 'running' || data?.state?.data?.runStatus === 'queued'
        ? 2000
        : false,
  });

  // When run transitions from running → terminal, refresh project so the list shows updated status
  useEffect(() => {
    const status = run?.runStatus;
    if (!status) return;
    const wasActive = prevStatusRef.current === 'running' || prevStatusRef.current === 'queued';
    const isNowDone = status === 'passed' || status === 'failed' || status === 'cancelled';
    if (wasActive && isNowDone) {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    }
    prevStatusRef.current = status;
  }, [run?.runStatus, projectId, queryClient]);

  const { data: steps } = useQuery({
    queryKey: ['run-steps', runIdNum],
    queryFn: () => api.getRunSteps(runIdNum),
    enabled: !!run,
  });

  const [expandedResult, setExpandedResult] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="p-8">
        <Skeleton className="h-8 w-64 mb-4" />
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!run) return null;

  const passRate = getPassRate(run.runPassedTests || 0, run.runTotalTests || 0);
  const isRunning = run.runStatus === 'running';

  return (
    <div className="p-8">
      {/* Back */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(`/projects/${projectId}`)}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.backToProject')}
      </Button>

      <PageHeader
        title={t('runDetail.title', { id: run.runId })}
        subtitle={`${run.runTrigger} trigger • ${run.runBranch || 'main'} ${run.runCommitSha ? `• ${run.runCommitSha.substring(0, 7)}` : ''}`}
        actions={
          <div className="flex items-center gap-3">
            <Badge
              variant={
                run.runStatus === 'passed'
                  ? 'success'
                  : run.runStatus === 'failed'
                    ? 'danger'
                    : run.runStatus === 'running'
                      ? 'info'
                      : 'outline'
              }
              className="text-sm px-4 py-1.5"
            >
              {isRunning && <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5 inline" />}
              {run.runStatus.toUpperCase()}
            </Badge>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Card className="text-center">
          <div className="text-3xl font-bold text-surface-100">{run.runTotalTests || 0}</div>
          <div className="text-xs text-surface-400">{t('runDetail.totalTests')}</div>
        </Card>
        <Card className="text-center">
          <div className="text-3xl font-bold text-success">{run.runPassedTests || 0}</div>
          <div className="text-xs text-surface-400 flex items-center justify-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> {t('runDetail.passedTests')}
          </div>
        </Card>
        <Card className="text-center">
          <div className="text-3xl font-bold text-danger">{run.runFailedTests || 0}</div>
          <div className="text-xs text-surface-400 flex items-center justify-center gap-1">
            <XCircle className="w-3 h-3" /> {t('runDetail.failedTests')}
          </div>
        </Card>
        <Card className="text-center">
          <div className="text-3xl font-bold text-surface-300">{run.runSkippedTests || 0}</div>
          <div className="text-xs text-surface-400 flex items-center justify-center gap-1">
            <SkipForward className="w-3 h-3" /> {t('runDetail.skippedTests')}
          </div>
        </Card>
        <Card className="text-center">
          <div className="text-3xl font-bold text-surface-100">
            {run.runDurationMs ? formatDuration(run.runDurationMs) : '—'}
          </div>
          <div className="text-xs text-surface-400 flex items-center justify-center gap-1">
            <Clock className="w-3 h-3" /> {t('runDetail.duration')}
          </div>
        </Card>
      </div>

      {/* Pass Rate Progress */}
      <Card className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-surface-300">{t('runDetail.passRateLabel')}</span>
          <span
            className={cn(
              'text-lg font-bold',
              passRate >= 90 ? 'text-success' : passRate >= 70 ? 'text-warning' : 'text-danger',
            )}
          >
            {passRate}%
          </span>
        </div>
        <ProgressBar
          value={passRate}
          max={100}
          color={passRate >= 90 ? 'success' : passRate >= 70 ? 'warning' : 'danger'}
        />
      </Card>

      {/* Test Results List */}
      <Card>
        <h3 className="text-lg font-semibold text-surface-100 mb-4">
          {t('runDetail.testResults')}
        </h3>

        {run.results && run.results.length > 0 ? (
          <div className="space-y-1">
            {run.results.map((result, idx) => {
              const isExpanded = expandedResult === result.resultId;
              const resultSteps = steps?.find((s) => s.resultId === result.resultId)?.steps || [];

              return (
                <motion.div
                  key={result.resultId}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.03 }}
                >
                  <div
                    onClick={() => setExpandedResult(isExpanded ? null : result.resultId)}
                    className={cn(
                      'flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all',
                      isExpanded
                        ? 'bg-surface-800/80'
                        : 'bg-surface-800/30 hover:bg-surface-800/50',
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {result.resultStatus === 'passed' ? (
                        <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                      ) : result.resultStatus === 'failed' ? (
                        <XCircle className="w-4 h-4 text-danger flex-shrink-0" />
                      ) : (
                        <SkipForward className="w-4 h-4 text-surface-500 flex-shrink-0" />
                      )}
                      <span className="text-sm text-surface-200 font-mono">
                        {result.resultTestName}
                      </span>
                      {result.resultRetryCount > 0 && (
                        <Badge variant="warning" className="text-[10px]">
                          {t('runDetail.retries', { count: result.resultRetryCount })}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-surface-500">
                        {result.resultDurationMs ? formatDuration(result.resultDurationMs) : ''}
                      </span>
                      <Badge variant="outline" className="text-[10px]">
                        {result.resultBrowser}
                      </Badge>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-surface-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-surface-500" />
                      )}
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      className="px-4 py-3 ml-7 border-l border-surface-700 space-y-3"
                    >
                      {/* Error */}
                      {result.resultErrorMessage && (
                        <div className="p-3 rounded-lg bg-danger/10 border border-danger/20">
                          <div className="flex items-center gap-2 mb-1">
                            <AlertTriangle className="w-4 h-4 text-danger" />
                            <span className="text-sm font-medium text-danger">
                              {t('runDetail.error')}
                            </span>
                          </div>
                          <p className="text-xs text-danger/80 font-mono whitespace-pre-wrap">
                            {result.resultErrorMessage}
                          </p>
                        </div>
                      )}

                      {/* Steps */}
                      {resultSteps.length > 0 && (
                        <div className="space-y-1">
                          <span className="text-xs text-surface-400 font-medium">
                            {t('runDetail.steps')}
                          </span>
                          {resultSteps.map((step) => (
                            <div
                              key={step.stepId}
                              className="flex items-center justify-between p-2 rounded bg-surface-800/30 text-xs"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-surface-500 w-5 text-right">
                                  {step.stepNumber}.
                                </span>
                                <Badge
                                  variant={step.stepStatus === 'passed' ? 'success' : 'danger'}
                                  className="text-[10px] px-1.5 py-0"
                                >
                                  {step.stepAction}
                                </Badge>
                                <span className="text-surface-300 font-mono">
                                  {step.stepTarget}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                {step.stepScreenshotUrl && (
                                  <Camera className="w-3 h-3 text-surface-400" />
                                )}
                                {step.stepDurationMs && (
                                  <span className="text-surface-500">
                                    {formatDuration(step.stepDurationMs)}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-surface-500 text-center py-8">
            {isRunning ? t('runDetail.waitingResults') : t('runDetail.noResults')}
          </p>
        )}
      </Card>
    </div>
  );
}
